import { useEffect, useLayoutEffect, useSyncExternalStore } from "react"
import { useLocation, useNavigationType } from "react-router-dom"

/**
 * Where you came from, so Back can say it and go there.
 *
 * Every in-app move is recorded with the title the page gave itself (PageHeader). Back then goes
 * one step back in the browser's own history — which keeps the list's tab, its search and its
 * scroll position — and is labelled with that page's name ("‹ To arrange", "‹ C-1668"). When there
 * is nothing behind (the app was opened from a notification or a shared link), Back goes to the
 * page's parent instead.
 *
 * Kept in sessionStorage, so it survives a reload of the tab and not much more.
 */
type Entry = { key: string; path: string; title: string }

const TRAIL_KEY = "hq-trail-v1"
const SCROLL_KEY = "hq-scroll-v1"
/** React Router's key for a page opened by its address rather than by a move inside the app. */
const FRESH = "default"

const load = <T,>(key: string, fallback: T): T => {
  try {
    const raw = sessionStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}
const save = (key: string, value: unknown) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Private mode or a full store: Back simply falls back to the parent page.
  }
}

let trail: Entry[] = load<Entry[]>(TRAIL_KEY, [])
const listeners = new Set<() => void>()
const emit = () => {
  save(TRAIL_KEY, trail.slice(-40))
  listeners.forEach((listener) => listener())
}

function record(key: string, path: string, type: "PUSH" | "POP" | "REPLACE") {
  const last = trail[trail.length - 1]
  if (last?.key === key) return
  if (type === "PUSH") {
    trail = [...trail, { key, path, title: "" }]
  } else if (type === "REPLACE") {
    // A tab switch on the same page (?queue=…) replaces the entry; the page's name stays.
    trail = [...trail.slice(0, -1), { key, path, title: last && last.path.split("?")[0] === path.split("?")[0] ? last.title : "" }]
  } else {
    // "default" is React Router's key for a page opened by its address (a link, a reload with no
    // history behind it): it is the same for every such page, so it never matches an older entry.
    const at = key === FRESH ? -1 : trail.findIndex((entry) => entry.key === key)
    trail = at >= 0 ? trail.slice(0, at + 1) : [{ key, path, title: "" }]
  }
  emit()
}

export function setTrailTitle(key: string, title: string) {
  const at = trail.findIndex((entry) => entry.key === key)
  if (at < 0 || trail[at].title === title) return
  trail = trail.map((entry, index) => (index === at ? { ...entry, title } : entry))
  emit()
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** The page before this one in the app, or null when there is none (opened from outside). */
export function usePrevious(): Entry | null {
  const entries = useSyncExternalStore(subscribe, () => trail)
  return entries.length > 1 ? entries[entries.length - 2] : null
}

const positions: Record<string, number> = load<Record<string, number>>(SCROLL_KEY, {})
// Every page opened by its address shares the key "default", so what an earlier visit left under it
// belongs to another page. Within this visit it is the first page, and is remembered like any other.
delete positions[FRESH]

/**
 * Records each move, and puts the page where it should be: at the top for a page opened anew, and
 * where you left it when you come back to one. Without this the browser kept the old scroll
 * position, so an order opened from far down a list opened halfway down the order.
 */
export function useNavigationMemory() {
  const location = useLocation()
  const type = useNavigationType()
  const path = `${location.pathname}${location.search}`

  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual"
  }, [])

  useLayoutEffect(() => {
    const previousPath = trail[trail.length - 1]?.path
    record(location.key, path, type)
    if (type === "POP") {
      restore(positions[location.key] ?? 0)
    } else if (location.hash) {
      toAnchor(decodeURIComponent(location.hash.slice(1)))
    } else if (type === "PUSH" || previousPath?.split("?")[0] !== location.pathname) {
      window.scrollTo(0, 0)
    }
    // Only a move to another place matters here; the path is part of the key.
  }, [location.key])

  useEffect(() => {
    const key = location.key
    let frame = 0
    const remember = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        positions[key] = window.scrollY
        save(SCROLL_KEY, positions)
      })
    }
    window.addEventListener("scroll", remember, { passive: true })
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener("scroll", remember)
    }
  }, [location.key])
}

/** A link to part of a page (/money#money-larry): waits for that part to be drawn, then shows it. */
function toAnchor(id: string) {
  window.scrollTo(0, 0)
  let tries = 0
  const attempt = () => {
    const target = document.getElementById(id)
    if (target) {
      target.scrollIntoView({ block: "start" })
      return
    }
    if (++tries < 150) requestAnimationFrame(attempt)
  }
  requestAnimationFrame(attempt)
}

/** Scrolls back to y once the page is tall enough (its list may still be drawing), for up to ~1.5s. */
function restore(y: number) {
  if (y <= 0) {
    window.scrollTo(0, 0)
    return
  }
  let tries = 0
  let stopped = false
  const stop = () => {
    stopped = true
  }
  // A finger on the screen wins over the restore.
  window.addEventListener("touchstart", stop, { once: true, passive: true })
  window.addEventListener("wheel", stop, { once: true, passive: true })
  const attempt = () => {
    if (stopped) return
    const room = document.documentElement.scrollHeight - window.innerHeight
    window.scrollTo(0, Math.min(y, Math.max(room, 0)))
    if (room >= y || ++tries > 90) {
      window.removeEventListener("touchstart", stop)
      window.removeEventListener("wheel", stop)
      return
    }
    requestAnimationFrame(attempt)
  }
  attempt()
}
